import {
  countRecords,
  getList,
  getLists,
  getPage,
  isVisibleListRecord,
  markLocalLookalikesRevealed,
  type LocalLead,
  type LocalList,
  type LocalPlace,
} from './localDb';
import type { Lead } from '../types/lead';
import type { Place } from '../types/place';
import type { ListModule } from '../config/constants';
import type {
  LeadsListResponse,
  PlacesListResponse,
  ScanType,
  SearchList,
} from '../types/searchList';
import { DEMO_VISIBLE_ITEMS } from '../lib/deface';
import { withoutEnrichment } from '../lib/localEnhance';

const LOCAL_PAGE_SIZE = 1000;

/**
 * Listas de negócios que o servidor já marcou como enriquecidas (legado ou outro aparelho). A
 * hidratação não traz a marca para a lista local; a listagem do servidor diz.
 */
const remoteEnrichedLists = new Set<string>();

function isEnrichedList(id: string, list?: Pick<LocalList, 'enrichedAt'>): boolean {
  return Boolean(list?.enrichedAt) || remoteEnrichedLists.has(id);
}

export function __resetRemoteEnrichedLists(): void {
  remoteEnrichedLists.clear();
}

/**
 * Perfil sem contato aguardando resolução. Contatos resolvidos preservam `lookalike: true`
 * como origem, portanto a visibilidade não pode depender desse flag [content.js:3602].
 */
export function isLookalikeLead(record: Pick<LocalLead, 'type'>): boolean {
  return record.type === 'profile';
}

const notLookalike = (record: LocalLead | LocalPlace): boolean => isVisibleListRecord(record);

const visibleForModule =
  (module: ScanType) =>
  (record: LocalLead | LocalPlace): boolean =>
    isVisibleListRecord(record, module);

/** Perfis sem contato (Emails e Telefones) e negócios tirados da lista (decisão 199) ficam fora. */
function hiddenRecordFilter(
  list: Pick<LocalList, 'module'>,
): typeof notLookalike | undefined {
  if (list.module !== 'emails' && list.module !== 'phones' && list.module !== 'places') {
    return undefined;
  }
  return visibleForModule(list.module);
}

function listMetadata(list: LocalList, count: number): SearchList {
  return {
    _id: list.id,
    scantype: list.module,
    keywords: list.keywords,
    sites: list.sites,
    domains: list.domains,
    countries: list.countries,
    locations: list.locations,
    distance: list.distance,
    startedAt: list.createdAt,
    count,
    lookalike: list.lookalikeAt ? true : undefined,
    enhanced: list.enrichedAt ? true : undefined,
  };
}

function hydrationMetadata(
  id: string,
  module: ScanType,
  count: number,
  createdAt: string | undefined,
): SearchList {
  return {
    _id: id,
    scantype: module,
    startedAt: createdAt ?? '1970-01-01T00:00:00.000Z',
    count,
  };
}

function toLead(record: LocalLead, demoVisible: boolean): Lead {
  const {
    listId,
    key,
    type,
    value,
    createdAt: _createdAt,
    emailStatus: _emailStatus,
    phoneStatus: _phoneStatus,
    lookalike: _lookalike,
    ...metadata
  } = record;

  return {
    ...metadata,
    _id: `${listId}:${key}`,
    _demoVisible: demoVisible,
    // Contato de Telefones da Rede do WhatsApp: o e-mail da empresa vem junto (decisão 457).
    email: type === 'email' ? value : metadata.email,
    phone: type === 'phone' ? value : undefined,
  };
}

/** Lista sem a marca de enriquecida mostra só o que veio do Maps (decisão 177). */
function toPlace(record: LocalPlace, demoVisible: boolean, enriched: boolean): Place {
  const {
    listId,
    key,
    createdAt: _createdAt,
    // Parecidos do Google, a pontuação e o nível servem à rodada de semelhantes, não à tela.
    similars: _similars,
    lookalikeScore: _lookalikeScore,
    lookalikeLevel: _lookalikeLevel,
    removedAt: _removedAt,
    enhancedAt: _enhancedAt,
    enhancedCount: _enhancedCount,
    mapsContacts: _mapsContacts,
    updatedAt,
    ...place
  } = enriched ? record : withoutEnrichment(record);

  return {
    ...place,
    _id: `${listId}:${key}`,
    _demoVisible: demoVisible,
    _localUpdatedAt: updatedAt ?? record.createdAt,
  };
}

function mapLeads(records: readonly LocalLead[], anchorKeys: readonly string[]): Lead[] {
  const visible = new Set(anchorKeys);
  return records.map((record) => toLead(record, visible.has(record.key)));
}

function mapPlaces(
  records: readonly LocalPlace[],
  anchorKeys: readonly string[],
  enriched: boolean,
): Place[] {
  const visible = new Set(anchorKeys);
  return records.map((record) => toPlace(record, visible.has(record.key), enriched));
}

/**
 * Os registros de lista continuam vindo do backend. Para ids também presentes nesta conta no
 * IndexedDB, a contagem real do store local vence o `count` persistido em ambos os lados.
 */
export async function mergeLocalListCounts(
  backendLists: readonly SearchList[],
  userId: string,
  module: ListModule,
): Promise<SearchList[]> {
  if (!userId || backendLists.length === 0) return [...backendLists];
  if (module === 'places') {
    for (const list of backendLists) if (list.enhanced === true) remoteEnrichedLists.add(list._id);
  }

  const backendIds = new Set(backendLists.map((list) => list._id));
  const localLists = (await getLists(userId)).filter(
    (list) => list.module === module && backendIds.has(list.id),
  );
  const localMetadata = new Map(
    await Promise.all(
      localLists.map(async (list) => [
        list.id,
        {
          count: await countRecords(list.module, list.id, hiddenRecordFilter(list)),
          enhanced: Boolean(list.enrichedAt),
          lookalike: Boolean(list.lookalikeAt),
        },
      ] as const),
    ),
  );

  return backendLists.map((list) => {
    const local = localMetadata.get(list._id);
    return local == null
      ? list
      : {
          ...list,
          count: local.count,
          enhanced: local.enhanced || list.enhanced,
          lookalike: local.lookalike || list.lookalike,
        };
  });
}

async function ownedLocalList(
  id: string,
  userId: string,
  kind: 'leads' | 'places',
): Promise<LocalList | undefined> {
  if (!userId) return undefined;
  const list = await getList(id);
  if (!list || list.userId !== userId) return undefined;
  if (kind === 'places') return list.module === 'places' ? list : undefined;
  return list.module !== 'places' ? list : undefined;
}

/** `undefined` significa que esta lista não é local desta conta e deve usar o backend legado. */
export async function getLocalLeadsPage(
  id: string,
  userId: string,
  offset: number,
): Promise<LeadsListResponse | undefined> {
  const list = await ownedLocalList(id, userId, 'leads');
  if (!list) return undefined;

  const page = await getPage<LocalLead>(list.module, id, {
    offset,
    limit: LOCAL_PAGE_SIZE,
    filter: hiddenRecordFilter(list),
    anchorLimit: DEMO_VISIBLE_ITEMS,
  });
  return {
    ...listMetadata(list, page.total),
    leads: mapLeads(page.records, page.anchorKeys),
  };
}

/** `undefined` significa que esta lista não é local desta conta e deve usar o backend legado. */
export async function getLocalPlacesPage(
  id: string,
  userId: string,
  offset: number,
): Promise<PlacesListResponse | undefined> {
  const list = await ownedLocalList(id, userId, 'places');
  if (!list) return undefined;

  const page = await getPage<LocalPlace>('places', id, {
    offset,
    limit: LOCAL_PAGE_SIZE,
    filter: hiddenRecordFilter(list),
    anchorLimit: DEMO_VISIBLE_ITEMS,
  });
  return {
    ...listMetadata(list, page.total),
    places: mapPlaces(page.records, page.anchorKeys, isEnrichedList(id, list)),
  };
}

/** As telas de lista materializam tudo; uma única varredura evita repetir o count por página. */
export async function getLocalLeadsAll(
  id: string,
  userId: string,
): Promise<LeadsListResponse | undefined> {
  const list = await ownedLocalList(id, userId, 'leads');
  if (!list) return undefined;
  const page = await getPage<LocalLead>(list.module, id, {
    filter: hiddenRecordFilter(list),
    anchorLimit: DEMO_VISIBLE_ITEMS,
  });
  return {
    ...listMetadata(list, page.total),
    leads: mapLeads(page.records, page.anchorKeys),
  };
}

export async function getLocalPlacesAll(
  id: string,
  userId: string,
): Promise<PlacesListResponse | undefined> {
  const list = await ownedLocalList(id, userId, 'places');
  if (!list) return undefined;
  const page = await getPage<LocalPlace>('places', id, {
    filter: hiddenRecordFilter(list),
    anchorLimit: DEMO_VISIBLE_ITEMS,
  });
  return {
    ...listMetadata(list, page.total),
    places: mapPlaces(page.records, page.anchorKeys, isEnrichedList(id, list)),
  };
}

/**
 * Torna páginas já confirmadas visíveis enquanto o marcador LocalList ainda não existe. A
 * propriedade da conta é garantida pelo gerenciador que iniciou a request autenticada; esta
 * leitura não publica a lista nem altera dirtyAt.
 */
export async function getHydratingLeads(
  id: string,
  module: Exclude<ScanType, 'places'>,
  _serverCount: number,
): Promise<LeadsListResponse> {
  const page = await getPage<LocalLead>(module, id, {
    filter: module === 'emails' || module === 'phones' ? visibleForModule(module) : undefined,
    anchorLimit: DEMO_VISIBLE_ITEMS,
  });
  return {
    ...hydrationMetadata(
      id,
      module,
      // O progresso continua usando `serverCount`; a tabela conta somente o que já é exibível.
      page.total,
      page.records[0]?.createdAt,
    ),
    leads: mapLeads(page.records, page.anchorKeys),
  };
}

/**
 * Contabiliza uma lista local para o preview: `total` já aparece e `lookalike` ainda está oculto.
 * `undefined` mantém o fallback legado para lista ausente, de outra conta ou de outro módulo.
 */
export async function getLocalLookalikeCounts(
  id: string,
  userId: string,
): Promise<{ total: number; lookalike: number } | undefined> {
  const list = await ownedLocalList(id, userId, 'leads');
  if (!list || (list.module !== 'emails' && list.module !== 'phones')) return undefined;

  const [total, lookalike] = await Promise.all([
    countRecords(list.module, id, visibleForModule(list.module)),
    countRecords(list.module, id, (record) =>
      isLookalikeLead(record as LocalLead)),
  ]);
  return { total, lookalike };
}

/**
 * Revela em lote apenas listas locais com semelhantes pendentes. A primitive do banco grava
 * `lookalikeAt` e `dirtyAt` na mesma transação; os registros em si já estão persistidos.
 */
export async function revealLocalLookalikes(
  ids: readonly string[],
  userId: string,
): Promise<string[]> {
  const counts = await Promise.all(
    [...new Set(ids)].map(async (id) => ({ id, counts: await getLocalLookalikeCounts(id, userId) })),
  );
  const pending = counts
    .filter(({ counts: result }) => (result?.lookalike ?? 0) > 0)
    .map(({ id }) => id);
  return markLocalLookalikesRevealed(pending, userId);
}

/** Perfis sem contato de uma lista, para o cruzamento por username (07-semelhantes.md §4.2). */
export async function getPendingLookalikeProfiles(
  id: string,
  userId: string,
): Promise<LocalLead[]> {
  const list = await ownedLocalList(id, userId, 'leads');
  if (!list || (list.module !== 'emails' && list.module !== 'phones') || list.lookalikeAt) return [];

  const page = await getPage<LocalLead>(list.module, id, {
    filter: (record) => (record as LocalLead).type === 'profile',
    limit: Number.MAX_SAFE_INTEGER,
  });
  return page.records;
}

export async function getHydratingPlaces(
  id: string,
  serverCount: number,
): Promise<PlacesListResponse> {
  const page = await getPage<LocalPlace>('places', id, {
    filter: visibleForModule('places'),
    anchorLimit: DEMO_VISIBLE_ITEMS,
  });
  return {
    ...hydrationMetadata(
      id,
      'places',
      Math.max(serverCount, page.total),
      page.records[0]?.createdAt,
    ),
    places: mapPlaces(page.records, page.anchorKeys, isEnrichedList(id)),
  };
}
